import { useState, useEffect, useCallback, useContext } from 'react';
import { toast } from 'react-toastify';

import api from '../services/api';
import { AuthContext } from '../context/Auth/AuthContext';

const useNappConfig = () => {
  const { user } = useContext(AuthContext);

  // State
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);

  // Load NAPP configuration
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/napp/config');
      setConfig(response.data);

      return response.data;
    } catch (error) {
      console.error('Erro ao carregar configuração NAPP:', error);

      // If config doesn't exist (404), return null instead of error
      if (error.response?.status === 404) {
        setConfig(null);
        return null;
      }

      setError(error.response?.data?.message || 'Erro ao carregar configuração');
      toast.error('Erro ao carregar configuração NAPP');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Save NAPP configuration
  const saveConfig = useCallback(async (configData) => {
    try {
      setSaving(true);
      setError(null);

      const response = await api.put('/napp/config', configData);
      setConfig(response.data);

      toast.success('Configuração salva com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      setError(error.response?.data?.message || 'Erro ao salvar configuração');
      toast.error('Erro ao salvar configuração: ' + (error.response?.data?.message || error.message));
      throw error;
    } finally {
      setSaving(false);
    }
  }, []);

  // Test NAPP connection
  const testConnection = useCallback(async (testConfig = null) => {
    try {
      setTesting(true);
      setError(null);

      const configToTest = testConfig || config;
      if (!configToTest) {
        throw new Error('Nenhuma configuração para testar');
      }

      const response = await api.post('/napp/test-connection', configToTest);

      toast.success('Conexão testada com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      const errorMessage = error.response?.data?.message || error.message;
      setError(errorMessage);
      toast.error('Erro na conexão: ' + errorMessage);
      throw error;
    } finally {
      setTesting(false);
    }
  }, [config]);

  // Create default configuration
  const createDefaultConfig = useCallback(() => {
    return {
      integrationToken: '',
      environment: 'staging',
      syncInterval: 30, // minutes
      enableNotifications: true,
      autoCreateContacts: true,
      autoCreateTickets: false,
      feedLimit: 100,
      notificationStatuses: [
        'PAYMENT_APPROVED',
        'IN_PREPARATION',
        'READY_TO_PICKUP',
        'SHIPMENT',
        'DELIVERED',
        'CANCELED'
      ],
      ticketStatuses: [
        'CANCELED',
        'RETURNED'
      ],
      active: true,
    };
  }, []);

  // Update configuration field
  const updateConfigField = useCallback((field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Toggle notification status
  const toggleNotificationStatus = useCallback((status) => {
    setConfig(prev => {
      const currentStatuses = prev?.notificationStatuses || [];
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter(s => s !== status)
        : [...currentStatuses, status];

      return {
        ...prev,
        notificationStatuses: newStatuses,
      };
    });
  }, []);

  // Toggle ticket creation status
  const toggleTicketStatus = useCallback((status) => {
    setConfig(prev => {
      const currentStatuses = prev?.ticketStatuses || [];
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter(s => s !== status)
        : [...currentStatuses, status];

      return {
        ...prev,
        ticketStatuses: newStatuses,
      };
    });
  }, []);

  // Validate configuration
  const validateConfig = useCallback((configData) => {
    const errors = [];

    if (!configData.integrationToken?.trim()) {
      errors.push('Token de integração é obrigatório');
    }

    if (!configData.environment) {
      errors.push('Ambiente é obrigatório');
    }

    if (!configData.syncInterval || configData.syncInterval < 1) {
      errors.push('Intervalo de sincronização deve ser maior que 0');
    }

    if (!configData.feedLimit || configData.feedLimit < 1 || configData.feedLimit > 1000) {
      errors.push('Limite do feed deve estar entre 1 e 1000');
    }

    return errors;
  }, []);

  // Check if configuration is valid
  const isConfigValid = useCallback(() => {
    if (!config) return false;
    const errors = validateConfig(config);
    return errors.length === 0;
  }, [config, validateConfig]);

  // Get configuration status
  const getConfigStatus = useCallback(() => {
    if (!config) return 'not_configured';
    if (!config.active) return 'inactive';
    if (!isConfigValid()) return 'invalid';
    return 'active';
  }, [config, isConfigValid]);

  // Get sync status info
  const getSyncInfo = useCallback(() => {
    if (!config) return null;

    return {
      lastSync: config.lastSyncAt,
      syncedOrders: config.syncedOrdersCount || 0,
      syncErrors: config.syncErrorsCount || 0,
      lastError: config.lastSyncError,
      nextSync: config.nextSyncAt,
    };
  }, [config]);

  // Check if notifications are enabled for status
  const isNotificationEnabledForStatus = useCallback((status) => {
    if (!config?.enableNotifications) return false;
    return config.notificationStatuses?.includes(status) || false;
  }, [config]);

  // Check if ticket creation is enabled for status
  const isTicketEnabledForStatus = useCallback((status) => {
    if (!config?.autoCreateTickets) return false;
    return config.ticketStatuses?.includes(status) || false;
  }, [config]);

  // Load configuration on mount
  useEffect(() => {
    if (user?.companyId) {
      loadConfig();
    }
  }, [loadConfig, user?.companyId]);

  // Auto-save configuration (debounced)
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);

  const scheduleAutoSave = useCallback((configData) => {
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    const timeout = setTimeout(() => {
      if (isConfigValid()) {
        saveConfig(configData);
      }
    }, 2000); // Auto-save after 2 seconds of inactivity

    setAutoSaveTimeout(timeout);
  }, [autoSaveTimeout, isConfigValid, saveConfig]);

  // Reset configuration to defaults
  const resetToDefaults = useCallback(() => {
    const defaultConfig = createDefaultConfig();
    setConfig(defaultConfig);
  }, [createDefaultConfig]);

  return {
    // Data
    config,
    loading,
    saving,
    testing,
    error,

    // Actions
    loadConfig,
    saveConfig,
    testConnection,
    updateConfigField,
    toggleNotificationStatus,
    toggleTicketStatus,
    resetToDefaults,

    // Helpers
    createDefaultConfig,
    validateConfig,
    isConfigValid,
    getConfigStatus,
    getSyncInfo,
    isNotificationEnabledForStatus,
    isTicketEnabledForStatus,
    scheduleAutoSave,
  };
};

export default useNappConfig;